import {useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useReactTable, getCoreRowModel} from '@tanstack/react-table';

import {
    SET_COLUMNS,
    SET_INSERT_COLUMN_ID,
    SET_SORTED,
    RESET_NEW_COLUMNS,
} from '../Redux/Actions/Types/queryToolActionTypes';
import {fetchQueryToolMetadata} from '../Redux/Thunks/queryToolThunks';
import {useQueryData, useGetPlaceholderGuid} from './QueryToolDataHooks';
import {useInsertColumns, useDeleteColumns} from './QueryToolColumnHooks';
import {useUpdateEffect} from './EffectHooks';
import {NAME_PLACEHOLDER} from '../Components/Pages/QueryTool';

export const useTableScrollbarEnhance = (dataTableRef) => {
    const {columns, data} = useSelector(state => state.queryToolState);

    const tbody = useRef(null);
    const theads = useRef([]);

    useEffect(() => {
        if (!dataTableRef) {
            return undefined;
        }

        theads.current = dataTableRef.getElementsByClassName('rt-thead');
        tbody.current = dataTableRef.getElementsByClassName('rt-tbody')[0];

        const handleScroll = () => {
            for (let i = 0; i < theads.current.length; i++) {
                theads.current.item(i).scrollLeft = tbody.current.scrollLeft;
            }
        };

        if (tbody.current) {
            tbody.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (tbody.current) {
                tbody.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [dataTableRef, columns, data]);

    useEffect(() => {
        if (tbody.current && tbody.current.scrollHeight > tbody.current.clientHeight) {
            for (let i = 0; i < theads.current.length; i++) {
                theads.current.item(i).classList.add('vertical-scrollbar-present');
            }
            return;
        }

        if (!tbody.current) {
            return;
        }

        for (let i = 0; i < theads.current.length; i++) {
            theads.current.item(i).classList.remove('vertical-scrollbar-present');
        }
    }, [dataTableRef, columns, data]);
};

export const useColumnDragAndResize = () => {
    const dispatch = useDispatch();
    const columns = useSelector(state => state.queryToolState.columns);

    const handleDragEnd = useCallback((result) => {
        const {source, destination} = result;

        if (!destination) return;
        if (source.index === destination.index) return;

        const columnsClone = [...columns];
        const [movedItem] = columnsClone.splice(source.index, 1);
        columnsClone.splice(destination.index, 0, movedItem);

        dispatch({type: SET_COLUMNS, columns: columnsClone});
    }, [columns, dispatch]);

    const handleResizeHover = useCallback((guid) => {
        if (!guid) return;
        dispatch({type: SET_INSERT_COLUMN_ID, insertColumnId: guid});
    }, [dispatch]);

    const handleResizeLeave = useCallback(() => {
        setTimeout(() => {
            dispatch({type: SET_INSERT_COLUMN_ID, insertColumnId: null});
        }, 150);
    }, [dispatch]);

    return {handleDragEnd, handleResizeHover, handleResizeLeave};
};

export const useSortColumns = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();

    const {sorted} = useSelector(state => state.queryToolState);

    return (event, field) => {
        let existingField = null;

        const defaultParams = {
            id: field,
            desc: false,
        };

        const sortedClone = sorted.map(x => {
            if (x.id === field) {
                existingField = {...x, desc: !x.desc};
                return existingField;
            }

            return x;
        });

        if (event.shiftKey) {
            if (existingField) {
                dispatch({type: SET_SORTED, sorted: sortedClone});
            } else {
                dispatch({type: SET_SORTED, sorted: [...sortedClone, defaultParams]});
            }
        } else {
            if (existingField) {
                dispatch({type: SET_SORTED, sorted: [existingField]});
            } else {
                dispatch({type: SET_SORTED, sorted: [defaultParams]});
            }
        }
        queryData();
    };
};

export const useQueryTableController = ({tableBodyRef}) => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const placeholderGuid = useGetPlaceholderGuid();
    const insertColumns = useInsertColumns();
    const deleteColumns = useDeleteColumns();

    const {
        columns,
        newColumns,
        data,
        count,
        page,
        pageSize,
        isStale,
        showAdd,
    } = useSelector(state => state.queryToolState);

    useEffect(() => {
        dispatch(fetchQueryToolMetadata());
    }, [dispatch]);

    useUpdateEffect(() => {
        queryData(false);
        if (tableBodyRef?.current) {
            tableBodyRef.current.scrollTop = 0;
        }
    }, [page, pageSize, queryData, tableBodyRef]);

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        defaultColumn: {size: 250},
        state: {
            pagination: {
                pageIndex: page,
                pageSize,
            },
        },
        pageCount: Math.ceil(count / pageSize) ?? -1,
        manualPagination: true,
    });

    const saveNewColumns = useCallback((guid) => {
        if (newColumns[0]?.data?.name === NAME_PLACEHOLDER) {
            deleteColumns([guid]);
            dispatch({type: RESET_NEW_COLUMNS});
        } else {
            dispatch({type: RESET_NEW_COLUMNS});
            queryData();
        }
    }, [deleteColumns, dispatch, newColumns, queryData]);

    const cancelAdd = useCallback(() => {
        const guids = newColumns.map(x => x.data.guid);

        if (guids.length > 0) {
            deleteColumns(guids);
        }
        dispatch({type: RESET_NEW_COLUMNS});
    }, [deleteColumns, dispatch, newColumns]);

    const handlePopoverHide = useCallback(() => {
        saveNewColumns(placeholderGuid);
    }, [placeholderGuid, saveNewColumns]);

    const handleAddColumnClick = useCallback(() => {
        insertColumns(null, [{name: NAME_PLACEHOLDER}]);
    }, [insertColumns]);

    return {
        table,
        columns,
        isStale,
        showAdd,
        saveNewColumns,
        cancelAdd,
        handlePopoverHide,
        handleAddColumnClick,
    };
};
